/* eslint-disable import/first */
/**
 * Welcome to the main entry point of the app. In this file, we'll
 * be kicking off our app.
 *
 * Most of this file is boilerplate and you shouldn't need to modify
 * it very often. But take some time to look through and understand
 * what is going on here.
 *
 * The app navigation resides in ./app/navigators, so head over there
 * if you're interested in adding screens and navigators.
 */
if (__DEV__) {
  // Load Reactotron in development only.
  // Note that you must be using metro's `inlineRequires` for this to work.
  // If you turn it off in metro.config.js, you'll have to manually import it.
  require("./devtools/ReactotronConfig.ts")
}

// Disable console logs in production for performance
if (!__DEV__) {
  console.log = () => {}
  console.info = () => {}
  console.debug = () => {}
  // Keep console.warn and console.error for critical issues
}

import "./utils/gestureHandler"

import { useEffect, useState } from "react"
import { Platform } from "react-native"
import { useFonts } from "expo-font"
import * as Linking from "expo-linking"
import { Analytics } from "@vercel/analytics/react"
import { KeyboardProvider } from "react-native-keyboard-controller"
import { initialWindowMetrics, SafeAreaProvider } from "react-native-safe-area-context"

import { initI18n } from "./i18n"
import { RootStoreProvider } from "./models"
import { AppNavigator } from "./navigators/AppNavigator"
import { useNavigationPersistence } from "./navigators/navigationUtilities"
import { ThemeProvider } from "./theme/context"
import { customFontsToLoad } from "./theme/typography"
import { initCrashReporting } from "./utils/crashReporting"
import { loadDateFnsLocale } from "./utils/formatDate"
import * as storage from "./utils/storage"

// Initialize Sentry as early as possible (web only, no-op on native)
initCrashReporting()

// Catch unhandled promise rejections for Sentry
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    import("@sentry/react").then((Sentry) => {
      Sentry.captureException(event.reason, {
        tags: { errorType: "unhandledrejection" },
      })
    })
  })
}

export const NAVIGATION_PERSISTENCE_KEY = "NAVIGATION_STATE"

// Web linking configuration
const prefix = Linking.createURL("/")
const config = {
  screens: {
    Entry: "",
    OrderTracking: {
      path: "track/:token",
      parse: {
        token: (token: string) => ({ token }),
      },
    },
    OrderTrackingSuccess: {
      path: "success/:token",
      parse: {
        token: (token: string) => ({ token }),
      },
    },
    FleetHubLogin: "fleethub/login",
    FleetHub: "fleethub",
    FleetHubOverview: "fleethub/overview",
  },
}

/**
 * This is the root component of our app.
 * @param {AppProps} props - The props for the `App` component.
 * @returns {JSX.Element} The rendered `App` component.
 */
export function App() {
  const {
    initialNavigationState,
    onNavigationStateChange,
    isRestored: isNavigationStateRestored,
  } = useNavigationPersistence(storage, NAVIGATION_PERSISTENCE_KEY)

  const [areFontsLoaded, fontLoadError] = useFonts(customFontsToLoad)
  const [isI18nInitialized, setIsI18nInitialized] = useState(false)

  useEffect(() => {
    initI18n()
      .then(() => setIsI18nInitialized(true))
      .then(() => loadDateFnsLocale())

    // Set document title for web
    if (Platform.OS === "web" && typeof document !== "undefined") {
      document.title = "HolDeinePlakette Tracking"
    }
  }, [])

  // Before we show the app, we have to wait for our state to be ready.
  // In the meantime, don't render anything. This will be the background
  // color set in native by rootView's background color.
  // In iOS: application:didFinishLaunchingWithOptions:
  // In Android: https://stackoverflow.com/a/45838109/204044
  // You can replace with your own loading component if you wish.
  if (!isNavigationStateRestored || !isI18nInitialized || (!areFontsLoaded && !fontLoadError)) {
    return null
  }

  const linking = {
    prefixes: [prefix],
    config,
  }

  // otherwise, we're ready to render the app
  return (
    <SafeAreaProvider initialMetrics={initialWindowMetrics}>
      <KeyboardProvider>
        <RootStoreProvider>
          <ThemeProvider>
            <AppNavigator
              linking={linking}
              initialState={initialNavigationState}
              onStateChange={onNavigationStateChange}
            />
            {Platform.OS === "web" && <Analytics />}
          </ThemeProvider>
        </RootStoreProvider>
      </KeyboardProvider>
    </SafeAreaProvider>
  )
}
